import {
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  ChangeDetectionStrategy,
} from '@angular/core';

import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { HttpClient } from '@angular/common/http';
import { Subject, takeUntil } from 'rxjs';
import { environment } from '../../../environment/environment';
import { CateringGroupDTO } from '../../../dto/catering/catering-group.dto';
import { DunaCateringGroupsCreateDialogComponent } from './duna-catering-groups-create-dialog.component';
import { DunaCateringGroupsEditDialogComponent } from './duna-catering-groups-edit-dialog.component';
import {
  DunaCateringGroupsMembersDialogComponent,
  DUNA_CATERING_GROUPS_MEMBERS_DIALOG_CONFIG,
} from './duna-catering-groups-members-dialog.component';
import { ConfirmResetDialogComponent } from '../duna-dashboard/confirm-reset-dialog.component';

@Component({
  selector: 'app-duna-catering-groups',
  standalone: true,
  imports: [
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatDialogModule,
    MatTableModule,
  ],
  templateUrl: './duna-catering-groups.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './duna-catering-groups.component.scss',
})
export class DunaCateringGroupsComponent implements OnInit, OnDestroy {
  /** Material table opdaterer mest stabilt via MatTableDataSource (ikke rå array). */
  readonly groupsDataSource = new MatTableDataSource<CateringGroupDTO>([]);
  loading = false;
  errorMessage: string | null = null;
  readonly displayedColumns = [
    'name',
    'description',
    'member_count',
    'actions',
  ];
  private readonly destroy$ = new Subject<void>();

  constructor(
    private http: HttpClient,
    private dialog: MatDialog,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.loadGroups();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  openCreateDialog(): void {
    this.dialog
      .open(DunaCateringGroupsCreateDialogComponent, { width: '480px' })
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((ok) => {
        if (ok) this.loadGroups(false);
      });
  }

  openEditDialog(group: CateringGroupDTO): void {
    this.dialog
      .open(DunaCateringGroupsEditDialogComponent, {
        width: '480px',
        data: { group },
      })
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((ok) => {
        if (ok) this.loadGroups(false);
      });
  }

  openMembersDialog(group: CateringGroupDTO): void {
    this.dialog
      .open(DunaCateringGroupsMembersDialogComponent, {
        ...DUNA_CATERING_GROUPS_MEMBERS_DIALOG_CONFIG,
        data: { group },
      })
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((ok) => {
        if (ok) this.loadGroups(false);
      });
  }

  deleteGroup(group: CateringGroupDTO): void {
    this.dialog
      .open(ConfirmResetDialogComponent, {
        width: '420px',
        data: {
          title: 'Slet forplejningsgruppe',
          message: `Er du sikker på, at du vil slette gruppen "${group.name}"? Event-tilknytninger fjernes.`,
          confirmText: 'Slet',
          cancelText: 'Annuller',
        },
      })
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((confirmed) => {
        if (!confirmed) return;
        this.http
          .delete(
            `${environment.apiUrl}/events/catering/groups/${group.catering_group_id}`
          )
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: () => this.loadGroups(false),
            error: (err) => {
              this.errorMessage =
                err?.error?.message || 'Kunne ikke slette gruppen.';
              this.cdr.detectChanges();
            },
          });
      });
  }

  private loadGroups(showLoading = true): void {
    if (showLoading) {
      this.loading = true;
    }
    this.errorMessage = null;

    this.http
      .get<unknown>(`${environment.apiUrl}/events/catering/groups`)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.groupsDataSource.data = this.parseGroupsResponse(res);
          this.loading = false;
          this.cdr.detectChanges();
        },
        error: (err) => {
          this.groupsDataSource.data = [];
          this.loading = false;
          this.errorMessage =
            err?.error?.message || 'Kunne ikke hente forplejningsgrupper.';
          this.cdr.detectChanges();
        },
      });
  }

  private parseGroupsResponse(res: unknown): CateringGroupDTO[] {
    if (Array.isArray(res)) {
      return res as CateringGroupDTO[];
    }
    if (res && typeof res === 'object') {
      const root = res as Record<string, unknown>;
      if (Array.isArray(root['items'])) {
        return root['items'] as CateringGroupDTO[];
      }
      const data = root['data'];
      if (
        data &&
        typeof data === 'object' &&
        Array.isArray((data as Record<string, unknown>)['items'])
      ) {
        return (data as { items: CateringGroupDTO[] }).items;
      }
    }
    return [];
  }
}
